import NewsletterForm from "components/NewsletterForm";
import Seo, { useSeo } from "components/Seo";
import ServiceTile from "components/ServiceTile";
import IconText from "components-lib/IconText";
import { BaseMarkdown } from "components-lib/Markdown";
import Section from "components-lib/Section";
import { graphql } from "gatsby";
import authorIcon from "images/icons/author.svg";
import calendarIcon from "images/icons/calendar.svg";
import { useLocale } from "internationalization";
import { isEmpty } from "lodash";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { FormattedDate, FormattedMessage } from "react-intl";
import { fullName } from "shared/utils";
import * as styles from "./Article.module.css";

/**
 * @param {import("gatsby").PageProps<Queries.ArticlePageQuery>} props
 */
function ArticlePage({ data }) {
  const { article, site, newsletterForm } = data;

  const locale = useLocale();

  const [shareURL, setShareURL] = useState({
    href: "https://semancin.sk",
    encoded: "https%3A%2F%2Fsemancin.sk",
  });

  useEffect(() => {
    setShareURL({
      href: window.location.href,
      encoded: window.encodeURIComponent(window.location.href),
    });
  }, []);

  useEffect(() => {
    const scriptElement = document.createElement("script");

    scriptElement.setAttribute("src", "https://platform.linkedin.com/in.js");
    scriptElement.setAttribute("type", "text/javascript");

    scriptElement.textContent = `lang: ${locale}`;

    document.body.appendChild(scriptElement);
  }, []);

  const { siteURL } = site?.siteMetadata ?? {};

  const ogImgUrl = article?.cover
    ? new URL(
        article.cover?.localFile?.publicURL || "",
        siteURL ?? "",
      ).toString()
    : "";

  const ogImgAlt = article?.cover ? article.cover.alternativeText : "";

  const seoProps = {
    data: article?.seo,
    fallback: {
      title: article?.title,
      description: article?.lead,
    },
    page: article?.fields?.slug,
  };

  const { canonical, title, description, keywords } = useSeo(seoProps);

  const [beforeNewsletterContent, afterNewsletterContent] =
    article?.content?.data?.content?.split("#newsletter-form") ?? [];

  return (
    <>
      <Seo {...seoProps} />
      <Helmet>
        <meta content="" property="fb:app_id" />
        <meta content={canonical} property="og:url" />
        <meta content="article" property="og:type" />
        <meta content={title} property="og:title" />
        <meta content={description} property="og:description" />
        {ogImgUrl && <meta content={ogImgUrl} property="og:image" />}
        {ogImgAlt && <meta content={ogImgAlt} property="og:image:alt" />}
      </Helmet>
      <Section className={styles.wrapper}>
        <article className={styles.article}>
          <header className={styles.header}>
            <h1 className={styles.title}>{article?.title}</h1>
            <section className={styles.info}>
              {article?.authors?.map((author, index) => (
                <IconText key={`article-author-${index}`} icon={authorIcon}>
                  {fullName(author)}
                </IconText>
              ))}
              {article?.date && (
                <IconText icon={calendarIcon}>
                  <FormattedDate value={article.date} />
                </IconText>
              )}
            </section>
            <section className={styles.shareButtons}>
              <iframe
                allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                allowFullScreen
                height="20"
                src={`https://www.facebook.com/plugins/share_button.php?href=${shareURL.encoded}&layout=button&size=small&width=77&height=20&appId`}
                style={{
                  border: "none",
                  overflow: "hidden",
                }}
                title="Facebook share"
                width="77"
              />
              <script data-url={shareURL.href} type="IN/Share" />
            </section>
            {!isEmpty(article?.services) && (
              <ul className={styles.services}>
                {article?.services?.map((service) => (
                  <ServiceTile
                    key={service?.strapi_id}
                    data={service}
                    type="compact"
                  />
                ))}
              </ul>
            )}
          </header>
          <section className={styles.content}>
            <BaseMarkdown>{beforeNewsletterContent}</BaseMarkdown>
            {afterNewsletterContent && (
              <>
                <NewsletterForm
                  data={newsletterForm}
                  className={styles.newsletterForm}
                  articleNewsletter={"article"}
                />
                <BaseMarkdown>{afterNewsletterContent}</BaseMarkdown>
              </>
            )}
          </section>
          {!isEmpty(keywords) && (
            <section>
              <p className={styles.keywords}>
                <span className={styles.keywordsTitle}>
                  <FormattedMessage id="keywords" />:{" "}
                </span>
                {keywords.map((keyword, index) => (
                  <span key={index} className={styles.keyword}>
                    {keyword}
                  </span>
                ))}
              </p>
            </section>
          )}
        </article>
      </Section>
    </>
  );
}

export const query = graphql`
  query ArticlePage($strapiId: Int!, $langKey: String!) {
    site {
      ...SiteMetadata
    }
    article: strapiArticle(
      strapi_id: { eq: $strapiId }
      locale: { eq: $langKey }
    ) {
      title
      lead
      content {
        data {
          content
        }
      }
      date
      cover {
        alternativeText
        localFile {
          publicURL
        }
      }
      fields {
        slug
      }
      authors {
        ...TeamMemberNameParts
        fields {
          slug
        }
      }
      services {
        ...ServiceTile
      }
      seo {
        ...Seo
      }
    }
    ...CommonQueries
  }
`;

export default ArticlePage;
